/**
 * use-onboarding-permissions.ts
 *
 * Thin permission hook for the Onboarding Permissions step.
 * Migrated in M5 to use the unified `check_permission`/`request_permission`/
 * `open_permission` commands (no more legacy shims).
 *
 * Uses `usePermission(kind)` from react-query for microphone, screenRecording,
 * and accessibility, so onboarding status always reflects the live OS state
 * via the 1s poll without any manual refresh calls.
 */
import * as Sentry from '@sentry/react';

import { usePermission } from './use-permission';
import type { PermissionStatus } from './types';

export type PermStatus = PermissionStatus;
export type AxStatus = 'unknown' | 'granted' | 'denied';

export interface OnboardingPermissions {
  micStatus: PermStatus | null;
  screenStatus: PermStatus | null;
  axStatus: AxStatus;
  requestMic: () => Promise<PermStatus | null>;
  requestScreen: () => Promise<PermStatus | null>;
  requestAccessibility: () => Promise<AxStatus>;
}

/** The slice of usePermission's surface that runRequest needs. */
type Perm = {
  status?: PermStatus;
  request: () => Promise<PermStatus>;
  open: () => Promise<void>;
};

/**
 * Run one permission's request flow: prompt, then open System Settings when the
 * result still isn't usable (`openWhen`). macOS shows the in-app prompt only
 * once, and screen recording / accessibility can only be toggled in Settings —
 * so for anything short of granted we route the user there. Fail-safe: never
 * throws; on error captures to Sentry and returns the last known status.
 */
async function runRequest(
  perm: Perm,
  kind: string,
  openWhen: (next: PermStatus) => boolean,
): Promise<PermStatus | null> {
  try {
    const next = await perm.request();
    if (openWhen(next)) await perm.open();
    return next;
  } catch (e) {
    console.error(`[onboarding] request_permission(${kind}) failed`, e);
    Sentry.captureException(e);
    return perm.status ?? null;
  }
}

export function useOnboardingPermissions(): OnboardingPermissions {
  const mic = usePermission('microphone');
  const screen = usePermission('screenRecording');
  const ax = usePermission('accessibility');

  const axStatus: AxStatus =
    ax.status === undefined
      ? 'unknown'
      : ax.status === 'granted'
        ? 'granted'
        : 'denied';

  // mic also opens Settings on notDetermined (its one-shot prompt won't reappear);
  // screen + accessibility open whenever the result isn't granted (Settings-only).
  const requestMic = () =>
    runRequest(mic, 'microphone', (n) => n === 'denied' || n === 'notDetermined');

  const requestScreen = () =>
    runRequest(screen, 'screenRecording', (n) => n !== 'granted');

  const requestAccessibility = async (): Promise<AxStatus> => {
    const next = await runRequest(ax, 'accessibility', (n) => n !== 'granted');
    if (next === null) return 'unknown';
    return next === 'granted' ? 'granted' : 'denied';
  };

  return {
    micStatus: mic.status ?? null,
    screenStatus: screen.status ?? null,
    axStatus,
    requestMic,
    requestScreen,
    requestAccessibility,
  };
}
